import React, { useState } from 'react';
import { Table, Tag, Modal, message, Popconfirm, Button, Space, Tooltip } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import type { TableColumnsType, TablePaginationConfig } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatTimestampToDateTime } from 'utils/dateTimeUtils';
import CommonConfigDrawer from './components/CommonConfigDrawer';
import CommonConfigFilter from './components/CommonConfigFilter';
import ConfigDebugModal from './components/ConfigDebugModal';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {
    GET_ALL_COMMON_CONFIGS_WITH_PAGINATION,
    BATCH_DELETE_COMMON_CONFIGS,
    BATCH_UPDATE_COMMON_CONFIG_STATUS,
    GetAllCommonConfigsWithPaginationResponse,
    BatchDeleteCommonConfigsResponse,
    BatchUpdateCommonConfigStatusResponse,
    CommonConfigWithOperatorType
} from './graphql/commonConfigQueries';

type DrawerMode = 'create' | 'edit' | 'view';

type CommonConfigPageProps = {
    initialConfigKey?: string;
};

const CommonConfigPage: React.FC<CommonConfigPageProps> = ({ initialConfigKey }) => {
    const { t } = useTranslation();

    // 状态管理
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerMode, setDrawerMode] = useState<DrawerMode>('create');
    const [currentConfigId, setCurrentConfigId] = useState<string | null>(null);
    const [debugModalVisible, setDebugModalVisible] = useState(false);
    const [debugConfigKey, setDebugConfigKey] = useState<string>('');

    // 分页状态
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortBy, setSortBy] = useState('updatedAt');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

    // 筛选状态（用户输入）
    const [searchKeyword, setSearchKeyword] = useState(initialConfigKey || '');
    const [filterDescription, setFilterDescription] = useState('');
    const [filterConfigType, setFilterConfigType] = useState<string | undefined>();
    const [filterIsActive, setFilterIsActive] = useState<boolean | undefined>();

    // 实际查询的筛选条件（点击筛选后才更新）
    const [activeFilters, setActiveFilters] = useState({
        configKey: initialConfigKey as string | undefined,
        description: undefined as string | undefined,
        configType: undefined as string | undefined,
        isActive: undefined as boolean | undefined
    });

    // 查询所有配置（分页）
    const { data, loading, refetch } = useQuery<GetAllCommonConfigsWithPaginationResponse>(
        GET_ALL_COMMON_CONFIGS_WITH_PAGINATION,
        {
            variables: {
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                    sortBy,
                    sortDirection
                },
                filters: activeFilters
            },
            fetchPolicy: 'network-only'
        }
    );

    // 批量删除
    const [batchDelete, { loading: deleteLoading }] = useMutation<BatchDeleteCommonConfigsResponse>(
        BATCH_DELETE_COMMON_CONFIGS,
        {
            onCompleted: (data) => {
                message.success(t('commonConfig.messages.deleteSuccess', { message: data.batchDeleteCommonConfigs.message }));
                setSelectedRowKeys([]);
                refetch();
            },
            onError: (error) => {
                message.error(t('commonConfig.messages.deleteFailed', { error: error.message }));
            }
        }
    );

    // 批量启用/禁用
    const [batchUpdateStatus, { loading: statusLoading }] = useMutation<BatchUpdateCommonConfigStatusResponse>(
        BATCH_UPDATE_COMMON_CONFIG_STATUS,
        {
            onCompleted: (data) => {
                message.success(t('commonConfig.messages.statusUpdateSuccess', { message: data.batchUpdateCommonConfigStatus.message }));
                setSelectedRowKeys([]);
                refetch();
            },
            onError: (error) => {
                message.error(t('commonConfig.messages.statusUpdateFailed', { error: error.message }));
            }
        }
    );

    // 获取分页数据
    const pageData = data?.getAllCommonConfigsWithPagination;
    const dataSource = pageData?.content || [];
    const totalElements = pageData?.totalElements || 0;

    // 表格列定义
    const columns: TableColumnsType<CommonConfigWithOperatorType> = [
        {
            title: t('commonConfig.table.columns.id'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
            fixed: 'left',
            align: 'left' as const,
            ellipsis: false,
            render: (id: string) => (
                <Button
                    type="link"
                    size="small"
                    onClick={() => handleView(id)}
                    style={{ paddingLeft: 0 }}
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{id}</span>
                </Button>
            )
        },
        {
            title: t('commonConfig.table.columns.configKey'),
            dataIndex: 'configKey',
            key: 'configKey',
            width: 200,
            fixed: 'left',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('commonConfig.table.columns.sortOrder'),
            dataIndex: 'sortOrder',
            key: 'sortOrder',
            width: 80,
            align: 'center' as const,
            render: (val: number) => <span>{val}</span>
        },
        {
            title: t('commonConfig.table.columns.matchRule'),
            dataIndex: 'matchRule',
            key: 'matchRule',
            width: 200,
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => {
                if (!text) return <span>-</span>;
                return (
                    <Tooltip
                        title={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{text}</div>}
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 480 }}
                    >
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200, cursor: 'help' }}>
                            {text}
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            title: t('commonConfig.table.columns.configValue'),
            dataIndex: 'configValue',
            key: 'configValue',
            width: 160,
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => {
                if (!text) return <span>-</span>;
                return (
                    <Tooltip
                        title={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{text}</div>}
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 480 }}
                    >
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160, cursor: 'help' }}>
                            {text}
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            title: t('commonConfig.table.columns.description'),
            dataIndex: 'description',
            key: 'description',
            width: 160,
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }} title={text}>
                    {text}
                </div>
            )
        },
        {
            title: t('commonConfig.table.columns.configType'),
            dataIndex: 'configType',
            key: 'configType',
            width: 120,
            align: 'left' as const,
            ellipsis: false,
            render: (type: string) => {
                const colorMap: Record<string, string> = {
                    STRING: 'blue',
                    JSON: 'purple',
                    NUMBER: 'orange',
                    BOOLEAN: 'green'
                };
                return <Tag color={colorMap[type] || 'default'}><span style={{ whiteSpace: 'nowrap' }}>{type}</span></Tag>;
            }
        },
        {
            title: t('commonConfig.table.columns.status'),
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            align: 'left' as const,
            ellipsis: false,
            render: (isActive: boolean) => {
                const text = isActive ? t('commonConfig.table.status.active') : t('commonConfig.table.status.inactive');
                return (
                    <Tag color={isActive ? 'success' : 'default'}>
                        <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                    </Tag>
                );
            }
        },
        {
            title: t('commonConfig.table.columns.updatedBy'),
            dataIndex: 'updatedByName',
            key: 'updatedByName',
            width: 120,
            align: 'left' as const,
            ellipsis: false,
            render: (name: string | undefined) => <span style={{ whiteSpace: 'nowrap' }}>{name || '-'}</span>
        },
        {
            title: t('commonConfig.table.columns.updatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            align: 'left' as const,
            ellipsis: false,
            sorter: true,
            render: (timestamp: number) => {
                const text = timestamp ? formatTimestampToDateTime(timestamp) : '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('commonConfig.table.columns.action'),
            key: 'action',
            width: 150,
            align: 'left' as const,
            fixed: 'right',
            render: (_, record) => (
                <div className="flex gap-2">
                    <Button
                        type="link"
                        size="small"
                        onClick={() => handleEdit(record.id)}
                        style={{ paddingLeft: 0 }}
                    >
                        <span style={{ whiteSpace: 'nowrap' }}>{t('commonConfig.table.actions.edit')}</span>
                    </Button>
                    {record.isActive ? (
                        <Button
                            type="link"
                            size="small"
                            danger
                            style={{ paddingLeft: 0 }}
                            onClick={() => rejectIfAnyActive([record.id])}
                        >
                            <span style={{ whiteSpace: 'nowrap' }}>{t('commonConfig.table.actions.delete')}</span>
                        </Button>
                    ) : (
                        <Popconfirm
                            title={t('commonConfig.confirm.delete.title')}
                            description={t('commonConfig.confirm.delete.content')}
                            onConfirm={() => handleDelete(record.id)}
                            okText={t('commonConfig.confirm.okText')}
                            cancelText={t('commonConfig.confirm.cancelText')}
                        >
                            <Button type="link" size="small" danger style={{ paddingLeft: 0 }}>
                                <span style={{ whiteSpace: 'nowrap' }}>{t('commonConfig.table.actions.delete')}</span>
                            </Button>
                        </Popconfirm>
                    )}
                </div>
            )
        }
    ];

    // 行选择配置
    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        }
    };

    // 处理新建
    const handleCreate = () => {
        setDrawerMode('create');
        setCurrentConfigId(null);
        setDrawerVisible(true);
    };

    // 处理编辑
    const handleEdit = (id: string) => {
        setDrawerMode('edit');
        setCurrentConfigId(id);
        setDrawerVisible(true);
    };

    // 处理查看
    const handleView = (id: string) => {
        setDrawerMode('view');
        setCurrentConfigId(id);
        setDrawerVisible(true);
    };

    // 公共确认弹窗方法
    const showConfirmModal = (title: string, content: string, onConfirm: () => Promise<void>) => {
        Modal.confirm({
            title,
            content,
            okText: t('commonConfig.confirm.okText'),
            cancelText: t('commonConfig.confirm.cancelText'),
            okButtonProps: {
                style: {
                    backgroundColor: '#722ed1',
                    borderColor: '#722ed1'
                }
            },
            onOk: onConfirm
        });
    };

    // 拒绝删除「启用」状态的行；返回 true 表示已拒绝并弹窗
    const rejectIfAnyActive = (ids: string[]): boolean => {
        const activeRows = dataSource.filter(item => ids.includes(item.id) && item.isActive);
        if (activeRows.length === 0) return false;
        const keys = activeRows.map(r => `${r.configKey}#${r.id}`).join('、');
        Modal.warning({
            title: t('commonConfig.confirm.activeDeleteBlocked.title'),
            content: t('commonConfig.confirm.activeDeleteBlocked.content', {
                count: activeRows.length,
                items: keys
            }),
            okText: t('commonConfig.confirm.okText'),
            okButtonProps: {
                style: { backgroundColor: '#722ed1', borderColor: '#722ed1' }
            }
        });
        return true;
    };

    // 处理删除
    const handleDelete = async (id: string) => {
        if (rejectIfAnyActive([id])) return;
        await batchDelete({ variables: { ids: [id] } });
    };

    // 处理批量删除
    const handleBatchDelete = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('commonConfig.messages.selectAtLeastOne'));
            return;
        }
        if (rejectIfAnyActive(selectedRowKeys as string[])) return;

        showConfirmModal(
            t('commonConfig.confirm.batchDelete.title'),
            t('commonConfig.confirm.batchDelete.content', { count: selectedRowKeys.length }),
            async () => {
                await batchDelete({
                    variables: { ids: selectedRowKeys as string[] }
                });
            }
        );
    };

    // 处理批量更改状态
    const handleBatchChangeStatus = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('commonConfig.messages.selectAtLeastOne'));
            return;
        }

        // 获取选中的配置项
        const selectedConfigs = dataSource.filter(item =>
            selectedRowKeys.includes(item.id)
        );

        // 检查选中项的状态
        const allActive = selectedConfigs.every(item => item.isActive);
        const allInactive = selectedConfigs.every(item => !item.isActive);

        if (!allActive && !allInactive) {
            message.warning(t('commonConfig.messages.selectSameStatus'));
            return;
        }

        // 根据当前状态决定操作
        const targetStatus = !allActive; // 如果全部启用则禁用，如果全部禁用则启用
        const confirmKey = targetStatus ? 'enable' : 'disable';

        showConfirmModal(
            t(`commonConfig.confirm.batchStatus.${confirmKey}.title`),
            t(`commonConfig.confirm.batchStatus.${confirmKey}.content`, { count: selectedRowKeys.length }),
            async () => {
                await batchUpdateStatus({
                    variables: {
                        ids: selectedRowKeys as string[],
                        isActive: targetStatus
                    }
                });
            }
        );
    };

    // 处理「调试配置」按钮：复用 selectedRowKeys。0 选警告；多 configKey 弹「请选择一个配置键」；单 key 打开 modal
    const handleDebugConfig = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('commonConfig.messages.selectAtLeastOne'));
            return;
        }
        const keys = new Set(
            dataSource
                .filter(item => selectedRowKeys.includes(item.id))
                .map(item => item.configKey)
        );
        if (keys.size > 1) {
            Modal.warning({
                title: t('commonConfig.debugConfig.multiKeyTitle'),
                content: t('commonConfig.debugConfig.multiKeyContent'),
                okText: t('commonConfig.confirm.okText'),
                okButtonProps: {
                    style: { backgroundColor: '#722ed1', borderColor: '#722ed1' }
                }
            });
            return;
        }
        setDebugConfigKey([...keys][0]);
        setDebugModalVisible(true);
    };

    // 处理抽屉关闭
    const handleDrawerClose = () => {
        setDrawerVisible(false);
        setCurrentConfigId(null);
    };

    // 处理抽屉成功回调
    const handleDrawerSuccess = () => {
        refetch();
    };

    // 处理表格变化（分页、排序）
    const handleTableChange = (
        pagination: TablePaginationConfig,
        _filters: any,
        sorter: any
    ) => {
        // 更新分页
        if (pagination.current) {
            setCurrentPage(pagination.current);
        }
        if (pagination.pageSize) {
            setPageSize(pagination.pageSize);
        }

        // 更新排序
        if (sorter.field && sorter.order) {
            setSortBy(sorter.field);
            setSortDirection(sorter.order === 'ascend' ? 'ASC' : 'DESC');
        }
    };

    // 处理筛选
    const handleFilter = () => {
        setCurrentPage(1); // 重置到第一页
        setActiveFilters({
            configKey: searchKeyword || undefined,
            description: filterDescription || undefined,
            configType: filterConfigType,
            isActive: filterIsActive
        });
    };

    // 重置筛选
    const handleResetFilters = () => {
        setSearchKeyword('');
        setFilterDescription('');
        setFilterConfigType(undefined);
        setFilterIsActive(undefined);
        setCurrentPage(1);
        setActiveFilters({
            configKey: undefined,
            description: undefined,
            configType: undefined,
            isActive: undefined
        });
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* 筛选和操作区域 */}
            <CommonConfigFilter
                searchKeyword={searchKeyword}
                filterDescription={filterDescription}
                filterConfigType={filterConfigType}
                filterIsActive={filterIsActive}
                onSearchKeywordChange={setSearchKeyword}
                onFilterDescriptionChange={setFilterDescription}
                onFilterConfigTypeChange={setFilterConfigType}
                onFilterIsActiveChange={setFilterIsActive}
                onFilter={handleFilter}
                onReset={handleResetFilters}
                selectedCount={selectedRowKeys.length}
                deleteLoading={deleteLoading}
                statusLoading={statusLoading}
                onCreateClick={handleCreate}
                onBatchDelete={handleBatchDelete}
                onBatchChangeStatus={handleBatchChangeStatus}
                onDebugConfig={handleDebugConfig}
            />

            {/* 表格 */}
            <Table
                columns={columns}
                dataSource={dataSource}
                rowKey="id"
                loading={loading}
                rowSelection={rowSelection}
                scroll={{ x: 'max-content' }}
                pagination={false}
                size="middle"
                tableLayout="auto"
             className="mb-4"/>

            {/* 分页 */}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => handleTableChange({ current: page, pageSize: size }, {}, {})}
                pageSizeOptions={['10', '20', '50', '100']}
            />

            {/* 抽屉 */}
            <CommonConfigDrawer
                visible={drawerVisible}
                onClose={handleDrawerClose}
                configId={currentConfigId}
                mode={drawerMode}
                onSuccess={handleDrawerSuccess}
            />

            {/* 调试配置弹窗 */}
            <ConfigDebugModal
                visible={debugModalVisible}
                configKey={debugConfigKey}
                onClose={() => setDebugModalVisible(false)}
                onConfigChanged={refetch}
            />
        </div>
    );
};

export default CommonConfigPage;

